import { joinString, removeEmptyValues } from '../shared/utils';
import { containersApi, containersApiBlob } from './api';
import { sortOptions } from './options';

const baseUri = 'customers';

export const CustomerAPI = {
  get: (customerId, details) =>
    containersApi.get(`${baseUri}/${customerId}`, {
      params: {
        details: details ? details : '',
      },
    }),
  addOrEditCustomer: (customer) => containersApi.put(baseUri, customer),
  getContacts: (customerId) =>
    containersApi.get(`${baseUri}/${customerId}/contacts`),
  getQuickNoteHistory: (customerId) =>
    containersApi.get(`${baseUri}/${customerId}/quickNotes`),
  //TODO: this calls the order entry api for search and result will be used as context for customer search
  lookupCustomersBySearchString: (searchString) =>
    containersApi.get(`${baseUri}/lookup?searchString=${searchString}`),
  getCustomerAddressOptions: (customerId) =>
    containersApi.get(`${baseUri}/${customerId}/addresses`),
  deleteCustomerAddress: (address) =>
    containersApi.delete(`${baseUri}/addresses/${address.id}`, {
      data: { customerId: address.customer_id },
    }),
  updateCustomerAddress: ({ address }) =>
    containersApi.put(`${baseUri}/addresses`, address),
  addCustomerAddress: ({ address }) =>
    containersApi.post(`${baseUri}/addresses`, address),
  verifyAddress: async (address) => {
    const response = await containersApi.put(
      `${baseUri}/addresses/verify`,
      address
    );
    if (response?.data.error) {
      throw new Error('Failed to verify address: ' + response?.data.error);
    }
    return response;
  },
  getSalesHistory: (customerId) =>
    containersApi.get(`${baseUri}/${customerId}/salesHistory`),
  getAccountOwners: (customerId) =>
    containersApi.get(`${baseUri}/${customerId}/accountOwners`),
  getRmas: (customerId) => containersApi.get(`${baseUri}/${customerId}/RMAs`),
  getItems: (customerId) => containersApi.get(`${baseUri}/${customerId}/items`),
  getInvoiceHistory: (customerId) =>
    containersApi.get(`${baseUri}/${customerId}/Invoices`),
  getQuotesByCustomerId: (customerId) =>
    containersApi.get(`${baseUri}/${customerId}/quotes`),
  getItemDetails: (customerId, itemId) =>
    containersApi.get(`${baseUri}/${customerId}/itemHistoryDetails/${itemId}`),
  getPayments: (customerId) =>
    containersApi.get(`${baseUri}/payments/?customerId=${customerId}`),
  getDocumentLinks: ({ customerId }) =>
    containersApi.get(`${baseUri}/${customerId}/documentLinks`),
  viewDocumentLinksFile: ({ customerId, rowIndex }) =>
    containersApiBlob.get(`${baseUri}/${customerId}/documentLinks/${rowIndex}`),
  addDocumentLinks: ({ updatedDocumentLinks, newDocumentLinks, customerId }) =>
    containersApi.post(`${baseUri}/${customerId}/documentLinks`, {
      updatedDocumentLinks,
      newDocumentLinks,
    }),
  getTaxExemptionCertificates: (customerId) =>
    containersApi.get(`${baseUri}/${customerId}/taxExemptionCertificates`),
  getContactLogs: (customerId) =>
    containersApi.get(`${baseUri}/${customerId}/contactLogs`),
  createContactLog: (contactLog) =>
    containersApi.post(
      `${baseUri}/${contactLog?.customer_id}/contactLogs`,
      contactLog
    ),
  lookupCustomerLastTouchData: ({
    rowsPerPage = 100,
    page = 1,
    searchString,
    consultantIds,
    freeAgencyOnly,
    customerId,
    statusFilters,
  } = {}) =>
    containersApi.get(`${baseUri}/lookup/lastTouch`, {
      params: removeEmptyValues({
        page,
        rowsPerPage,
        freeAgencyOnly: freeAgencyOnly ? '1' : '',
        customerId: customerId || '',
        searchString,
        consultantIds: consultantIds?.join(',') || '',
        statusFilters,
      }),
    }),
  updateCustomerAccountTrackerNotes: ({ customerId, notes }) => {
    return containersApi.put(`${baseUri}/${customerId}/accountTrackerNotes`, {
      notes,
    });
  },
  updateCustomerLastTouchDetails: ({
    customerId,
    contactBand,
    claimCustomer,
  }) =>
    containersApi.put(`${baseUri}/${customerId}/lastTouch`, {
      contactBand,
      claimCustomer,
    }),
  customerCreditHealth: (customerId) =>
    containersApi.get(`${baseUri}/${customerId}/creditHealth`),
  updateErpCustomer: ({ customerId, customer }) =>
    containersApi.put(`${baseUri}/${customerId}/erpCustomer`, {
      customer,
    }),
  submitCreditMemoRequest: ({ updatedCustomer }) =>
    containersApi.post(`${baseUri}/creditMemo`, updatedCustomer),
  requestCustomerMove: ({ customerId, note, assignTo }) =>
    containersApi.post(`${baseUri}/send-to`, {
      customerId,
      note,
      assignTo,
    }),
  approveCustomerMove: ({ customerId }) =>
    containersApi.post(`${baseUri}/confirm-send-to`, {
      customerId,
    }),
  denyCustomerMove: ({ customerId }) =>
    containersApi.post(`${baseUri}/deny-send-to`, {
      customerId,
    }),
  reassignCustomer: ({ customerId, userId, note = undefined }) =>
    containersApi.post(`${baseUri}/reassign`, {
      customerId,
      userId,
      note,
    }),
};

export const CustomerCarrierAccountApi = {
  create: (customerCarrierAccount) =>
    containersApi.post(`${baseUri}/CarrierAccounts`, customerCarrierAccount),
  get: (customerId) =>
    containersApi.get(`${baseUri}/CarrierAccounts?customerId=${customerId}`),
  delete: ({ customerId, customerCarrierAccountId }) =>
    containersApi.delete(
      `${baseUri}/CarrierAccounts/${
        customerCarrierAccountId || ''
      }?customerId=${customerId || ''}`
    ),
};

//TODO: use this function to get customer lookup options in customer page
export const getCustomerLookupOptions = async ({ searchString }) => {
  const { data: customers } = await CustomerAPI.lookupCustomersBySearchString(
    searchString
  );

  customers?.forEach((c) => {
    c.type = 'customer';
    c.value = c.customer_id;
    c.label = joinString([
      `${c.customer_name} (${c.customer_id})`,
      c.consultant_name,
      //Additional Customer Properties
      joinString([c.contact_name, c.contact_email, c.contact_phone], ' | '),
      joinString(
        [c.mail_address1, c.mail_city, c.mail_state, c.mail_postal_code],
        ', '
      ),
    ]);
  });

  return customers;
};

export const getCustomerAddressOptions = async (customerId) => {
  const { data: customerAddresses } =
    await CustomerAPI.getCustomerAddressOptions(customerId);

  customerAddresses?.forEach((ca) => {
    ca.value = `${ca.id}`;

    const defaultDescription =
      (ca.is_default_billing &&
        ca.is_default_shipping &&
        '(*Default billing & shipping)') ||
      (ca.is_default_billing && '(*Default billing)') ||
      (ca.is_default_shipping && '(*Default shipping)') ||
      '';

    ca.label = joinString([
      defaultDescription,
      ca.name + ` (ID: ${ca.id})`,
      ca.phys_address1,
      ca.phys_address2,
      joinString(
        [ca.phys_city, ca.phys_state, ca.phys_postal_code, ca.phys_country],
        ', '
      ),
    ]);
  });

  return sortOptions(customerAddresses);
};
